/**
 * @module beebee-base/lib/utility/province
 * @description Italian province (provincia) code normalization.
 *
 * Italian addresses store the province as the 2-letter ISTAT/vehicle code
 * (`OR`, `MI`, `RM`) in `address.location_address_county`. Several fill paths
 * receive the EXTENDED name instead — HERE Maps returns
 * `county: "Oristano"` / `county: "Città Metropolitana di Milano"`, and the
 * province input is rendered `disabled` for Italian addresses, so a long value
 * written there can never be corrected by the user and travels into invoices,
 * FSE payloads and SDI XML. This module maps any extended form back to the
 * 2-letter code; unknown values resolve to `null` so the caller can fall back
 * to the `comuni_search` lookup rather than persisting garbage.
 */

// Canonical province names → code. 107 current provinces / metropolitan cities.
const PROVINCE_BY_CANONICAL_NAME = {
  // Abruzzo
  "L'Aquila": 'AQ',
  Chieti: 'CH',
  Pescara: 'PE',
  Teramo: 'TE',
  // Basilicata
  Matera: 'MT',
  Potenza: 'PZ',
  // Calabria
  Catanzaro: 'CZ',
  Cosenza: 'CS',
  Crotone: 'KR',
  'Reggio Calabria': 'RC',
  'Vibo Valentia': 'VV',
  // Campania
  Avellino: 'AV',
  Benevento: 'BN',
  Caserta: 'CE',
  Napoli: 'NA',
  Salerno: 'SA',
  // Emilia-Romagna
  Bologna: 'BO',
  Ferrara: 'FE',
  'Forlì-Cesena': 'FC',
  Modena: 'MO',
  Parma: 'PR',
  Piacenza: 'PC',
  Ravenna: 'RA',
  'Reggio Emilia': 'RE',
  Rimini: 'RN',
  // Friuli-Venezia Giulia
  Gorizia: 'GO',
  Pordenone: 'PN',
  Trieste: 'TS',
  Udine: 'UD',
  // Lazio
  Frosinone: 'FR',
  Latina: 'LT',
  Rieti: 'RI',
  Roma: 'RM',
  Viterbo: 'VT',
  // Liguria
  Genova: 'GE',
  Imperia: 'IM',
  'La Spezia': 'SP',
  Savona: 'SV',
  // Lombardia
  Bergamo: 'BG',
  Brescia: 'BS',
  Como: 'CO',
  Cremona: 'CR',
  Lecco: 'LC',
  Lodi: 'LO',
  Mantova: 'MN',
  Milano: 'MI',
  'Monza e della Brianza': 'MB',
  Pavia: 'PV',
  Sondrio: 'SO',
  Varese: 'VA',
  // Marche
  Ancona: 'AN',
  'Ascoli Piceno': 'AP',
  Fermo: 'FM',
  Macerata: 'MC',
  'Pesaro e Urbino': 'PU',
  // Molise
  Campobasso: 'CB',
  Isernia: 'IS',
  // Piemonte
  Alessandria: 'AL',
  Asti: 'AT',
  Biella: 'BI',
  Cuneo: 'CN',
  Novara: 'NO',
  Torino: 'TO',
  'Verbano-Cusio-Ossola': 'VB',
  Vercelli: 'VC',
  // Puglia
  Bari: 'BA',
  'Barletta-Andria-Trani': 'BT',
  Brindisi: 'BR',
  Foggia: 'FG',
  Lecce: 'LE',
  Taranto: 'TA',
  // Sardegna
  Cagliari: 'CA',
  Nuoro: 'NU',
  Oristano: 'OR',
  Sassari: 'SS',
  'Sud Sardegna': 'SU',
  // Sicilia
  Agrigento: 'AG',
  Caltanissetta: 'CL',
  Catania: 'CT',
  Enna: 'EN',
  Messina: 'ME',
  Palermo: 'PA',
  Ragusa: 'RG',
  Siracusa: 'SR',
  Trapani: 'TP',
  // Toscana
  Arezzo: 'AR',
  Firenze: 'FI',
  Grosseto: 'GR',
  Livorno: 'LI',
  Lucca: 'LU',
  'Massa-Carrara': 'MS',
  Pisa: 'PI',
  Pistoia: 'PT',
  Prato: 'PO',
  Siena: 'SI',
  // Trentino-Alto Adige
  Bolzano: 'BZ',
  Trento: 'TN',
  // Umbria
  Perugia: 'PG',
  Terni: 'TR',
  // Valle d'Aosta
  Aosta: 'AO',
  // Veneto
  Belluno: 'BL',
  Padova: 'PD',
  Rovigo: 'RO',
  Treviso: 'TV',
  Venezia: 'VE',
  Verona: 'VR',
  Vicenza: 'VI'
};

// Alternate spellings actually seen in the wild: historic/administrative names,
// bilingual names, and the English exonyms HERE returns when the request is not
// localized to Italian.
const PROVINCE_ALIASES = {
  Aquila: 'AQ',
  'Reggio di Calabria': 'RC',
  "Reggio nell'Emilia": 'RE',
  'Reggio Emilia (Reggio nell’Emilia)': 'RE',
  'Forli-Cesena': 'FC',
  'Forlì Cesena': 'FC',
  'Massa Carrara': 'MS',
  'Massa e Carrara': 'MS',
  'Monza e Brianza': 'MB',
  'Monza-Brianza': 'MB',
  'Verbano Cusio Ossola': 'VB',
  'Barletta Andria Trani': 'BT',
  'Pesaro-Urbino': 'PU',
  'Pesaro e Urbino (Urbino)': 'PU',
  'Bolzano/Bozen': 'BZ',
  Bozen: 'BZ',
  'Alto Adige': 'BZ',
  Südtirol: 'BZ',
  'South Tyrol': 'BZ',
  Trient: 'TN',
  "Valle d'Aosta": 'AO',
  "Vallée d'Aoste": 'AO',
  Aoste: 'AO',
  'Roma Capitale': 'RM',
  Rome: 'RM',
  Milan: 'MI',
  Turin: 'TO',
  Florence: 'FI',
  Venice: 'VE',
  Naples: 'NA',
  Genoa: 'GE',
  Padua: 'PD',
  Mantua: 'MN',
  Syracuse: 'SR',
  Mailand: 'MI',
  Rom: 'RM',
  Neapel: 'NA',
  Turijn: 'TO',
  // Sardinian provinces merged in 2016: legacy records and third-party payloads
  // still carry them, and their code is still 2 characters — keep them
  // recognized so a normalization pass does not blank a stored value.
  'Carbonia-Iglesias': 'CI',
  'Medio Campidano': 'VS',
  Ogliastra: 'OG',
  'Olbia-Tempio': 'OT'
};

// Administrative prefixes HERE and public registries prepend to the bare name.
const PREFIXES = ['libero consorzio comunale di', 'citta metropolitana di', 'provincia autonoma di', 'provincia autonoma', 'provincia di', 'provincia del', 'provincia dell', 'provincia della', 'metropolitan city of', 'province of'];

/**
 * Lowercases, strips diacritics and collapses every non-alphanumeric run to a
 * single space, then removes any administrative prefix. Applied to both the
 * table keys and the incoming value so `"Città Metropolitana di Milano"`,
 * `"CITTA' METROPOLITANA DI MILANO"` and `"Milano"` all hit the same entry.
 * @param {string} value
 * @returns {string}
 */
function slug(value) {
  let out = String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  for (const prefix of PREFIXES) {
    if (out.startsWith(`${prefix} `)) {
      out = out.slice(prefix.length + 1).trim();
      break;
    }
  }
  return out;
}
const PROVINCE_BY_SLUG = {};
for (const [name, code] of Object.entries(PROVINCE_BY_CANONICAL_NAME)) {
  PROVINCE_BY_SLUG[slug(name)] = code;
}
for (const [name, code] of Object.entries(PROVINCE_ALIASES)) {
  PROVINCE_BY_SLUG[slug(name)] = code;
}

/** Every code the tables know about, current and merged. @type {Set<string>} */
const PROVINCE_CODES = new Set(Object.values(PROVINCE_BY_SLUG));

/**
 * True when `value` is already a known 2-letter province code.
 * @param {*} value
 * @returns {boolean}
 */
function isProvinceCode(value) {
  return typeof value === 'string' && PROVINCE_CODES.has(value.trim().toUpperCase());
}

/**
 * Normalizes an Italian province to its 2-character code.
 *
 * - `'or'` / `'OR'` → `'OR'`
 * - `'Oristano'` → `'OR'`
 * - `'Città Metropolitana di Milano'` → `'MI'`
 * - any other 2-letter string → uppercased and returned unchanged (never widen
 *   a value that already satisfies the 2-char invariant)
 * - anything unrecognized (empty, a region, a foreign county) → `null`
 *
 * @param {*} value
 * @returns {string|null} 2-letter code, or null when it cannot be resolved
 */
function normalizeProvinceCode(value) {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  if (/^[a-z]{2}$/i.test(raw)) return raw.toUpperCase();
  return PROVINCE_BY_SLUG[slug(raw)] ?? null;
}

export { PROVINCE_BY_CANONICAL_NAME, PROVINCE_CODES, isProvinceCode, normalizeProvinceCode };
//# sourceMappingURL=province.js.map
